import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from 'utils/axios';
import { setSession } from 'utils/jwt';
import { ACTIONS } from 'utils/constants';

import API from 'restApi';
import useSessionStorage from 'hooks/useSessionStorage';
import { encrypt, decrypt } from 'utils/crypto';

// ----------------------------------------------------------------------
const initialState = {
  isAuthenticated: false,
  isPrivateTheme: false,
  isMustResetPassword: false,
  authorizedIpp: null,
  isInitialized: false,
  user: null,
  error: null,
  actorsSelected: [],
  userActorsTypeCount: null,
  filters: [],
  allActorsSelected: false,
  allFavoriteActorsSelected: false,
  permissions: [],
  profile: null

};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, userActorsTypeCount, actorsSelected, filters, allActorsSelected, allFavoriteActorsSelected, permissions, profile, isPrivateTheme, authorizedIpp } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      permissions,
      profile,
      actorsSelected,
      userActorsTypeCount,
      allActorsSelected,
      allFavoriteActorsSelected,
      filters,
      isPrivateTheme,
      authorizedIpp
    };
  },
  LOGIN: (state, action) => {
    const { user,
      allActorsSelected,
      allFavoriteActorsSelected,
      userActorsTypeCount,
      filters,
      permissions,
      profile,
      isPrivateTheme,
      authorizedIpp } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      permissions,
      userActorsTypeCount,
      allActorsSelected,
      allFavoriteActorsSelected,
      filters,
      profile,
      isPrivateTheme,
      authorizedIpp,
      isMustResetPassword: false
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isPrivateTheme: false,
    isMustResetPassword: false,
    authorizedIpp: null,
    error: null,
    user: null
  }),
  TOGGLERESETPASSWORD: (state, action) => {
    const { isMustResetPassword, user } = action.payload;
    return {
      ...state,
      isMustResetPassword,
      user
    };
  },
  ACTORTYPESLIST: (state, action) => {
    const { actorsSelected, allActorsSelected, allFavoriteActorsSelected, userActorsTypeCount } = action.payload;
    return {
      ...state,
      actorsSelected,
      allActorsSelected,
      allFavoriteActorsSelected,
      userActorsTypeCount
    };
  },
  ACTORTYPESCOUNT: (state, action) => {
    const { userActorsTypeCount } = action.payload;
    return {
      ...state,
      userActorsTypeCount
    };
  },
  ERROR: (state, action) => ({
    ...state,
    error: action.payload
  }),
  UPDATEUSERDEFAULTDELAYDAYS: (state, action) => ({
    ...state,
    user: {
      ...state.user,
      userDefaultDeltaDays: action.payload.userDefaultDeltaDays
    }
  }),
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),

});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [store, setStore] = useSessionStorage('storage');
  // eslint-disable-next-line
  // const [connecteTime, setConnecteTime] = useSessionStorage('connecteTime');

  const connected = decrypt(store);

  useEffect(() => {
    const initialize = async () => {
      try {
        const { user, filters, allActorsSelected, allFavoriteActorsSelected, profile, permissions, actorsSelected, userActorsTypeCount, isPrivateTheme, authorizedIpp } = window.sessionStorage.getItem('storage') ? decrypt(JSON.parse(window.sessionStorage.getItem('storage'))) : {};

        if (user) {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
              actorsSelected,
              userActorsTypeCount,
              permissions,
              filters,
              allActorsSelected,
              allFavoriteActorsSelected,
              profile,
              isPrivateTheme,
              authorizedIpp
            }
          });
        } else {
          setSession(null);

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(API.path, {
        "login": email,
        "password": password,
        "error": "",
        "loading": false,
        "showPassword": false,
        "action": "LOGIN"
      });

      const { user, functions: permissions, profile, statut, reason } = response.data;

      if (statut === 'KO') {
        if (reason === "PASSWORDEXPIRED")
          toggleResetPassword({ user, isMustResetPassword: true });
        throw new Error(reason);
      }
      const {
        allActorsSelected,
        allFavoriteActorsSelected,
        userActorsTypeCount
      } = await getCurrentUserActors(user.usercode);

      const encryptedData = encrypt({
        user,
        permissions,
        profile,
        allActorsSelected,
        allFavoriteActorsSelected,
        actorsSelected: [],
        userActorsTypeCount
      });
      setStore(encryptedData);

      dispatch({
        type: 'LOGIN',
        payload: {
          user,
          permissions,
          profile,
          allActorsSelected,
          allFavoriteActorsSelected,
          actorsSelected: [],
          userActorsTypeCount
        }
      });
    } catch (e) {
      dispatch({
        type: 'ERROR',
        payload: "INCORRECTPASSWORD"
      });
    }

  };

  const directAccess = async ({ email, ippValue, nohosp, isBadRequest = false, isPrivateTheme = true }) => {
    try {
      if (isBadRequest) {
        throw new Error("URLINVALID");
      }
      const response = await axios.post(API.path, {
        criteres: { loginCode: email, ipp: ippValue || "", hospCode: nohosp || "" },
        action: "LOGINDIRECT"
      });

      const { user, functions: permissions, reason, statut, profile, ipp } = response.data;

      if (statut !== 'OK') {
        throw new Error(reason);
      }
      const {
        allActorsSelected,
        allFavoriteActorsSelected,
        userActorsTypeCount
      } = await getCurrentUserActors(user.usercode);

      const encryptedData = encrypt({
        user,
        permissions,
        profile,
        allActorsSelected,
        allFavoriteActorsSelected,
        actorsSelected: [],
        userActorsTypeCount,
        isPrivateTheme,
        authorizedIpp: ipp
      });
      setStore(encryptedData);
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
          permissions,
          profile,
          allActorsSelected,
          allFavoriteActorsSelected,
          actorsSelected: [],
          userActorsTypeCount,
          isPrivateTheme,
          authorizedIpp: ipp
        }
      });

    } catch (e) {
      dispatch({
        type: 'ERROR',
        payload: e.message
      });
    }

  };

  const logout = async (code) => {
    await axios.post(API.path, {
      usercode: code,
      action: ACTIONS.LOGOUT
    });
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const toggleResetPassword = async ({ isMustResetPassword, user }) => {
    dispatch({
      type: 'TOGGLERESETPASSWORD', payload: {
        isMustResetPassword,
        user
      }
    });
  };


  const setCurrentUserActorsAndFilter = async ({ actorsSelected, allActorsSelected, allFavoriteActorsSelected }) => {
    dispatch({
      type: 'ACTORTYPESLIST',
      payload: {
        actorsSelected,
        allActorsSelected,
        allFavoriteActorsSelected
      }
    });
    setStore(encrypt({
      ...connected,
      actorsSelected,
      allActorsSelected,
      allFavoriteActorsSelected

    }));
  };


  const updateUserDefaultDeltaDays = async (days) => {
    dispatch({
      type: 'UPDATEUSERDEFAULTDELAYDAYS',
      payload: {
        userDefaultDeltaDays: days
      }
    });
    setStore(encrypt({
      ...connected,
      user: {
        ...connected.user,
        userDefaultDeltaDays: days
      }
    }));
  };
  const setCurrentUserActorTypeCount = async ({ userActorsTypeCount }) => {
    dispatch({
      type: 'ACTORTYPESCOUNT',
      payload: {
        userActorsTypeCount
      }
    });
    setStore(encrypt({
      ...connected,
      userActorsTypeCount
    }));
  };

  async function getCurrentUserActors(staffCode) {
    try {
      let allActorsSelected = false;
      let allFavoriteActorsSelected = false;

      const { data: { totalFavM,
        totalFavP,
        totalFavU,
        totalM,
        totalP,
        totalU,
        actorsForUser } } = await axios.post(API.path,
          {
            action: ACTIONS.INFOSACTORSFORUSER,
            userCode: staffCode,
          }
        );

      if ((totalFavM + totalFavP + totalFavU) > 0) {
        allFavoriteActorsSelected = true;
      }
      else {
        allActorsSelected = true;
      }
      return ({
        allActorsSelected,
        allFavoriteActorsSelected,
        userActorsTypeCount: { actors: actorsForUser, P: { total: totalP, favoritesTotal: totalFavP }, M: { total: totalM, favoritesTotal: totalFavM }, U: { total: totalU, favoritesTotal: totalFavU } }
      });

    } catch (error) {
      console.log(error);
      return error;
    }

  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        actorsSelected: connected?.actorsSelected,
        userActorsTypeCount: connected?.userActorsTypeCount,
        filters: connected?.filters,
        method: 'jwt',
        login,
        logout,
        directAccess,
        setCurrentUserActorsAndFilter,
        toggleResetPassword,
        getCurrentUserActors,
        setCurrentUserActorTypeCount,
        updateUserDefaultDeltaDays
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };